import type { CmlContent } from '@coursera-web/cml';

import type { FilterType, Filters } from 'bundles/enterprise-admin-drawers/components/types';
import type EnterpriseScheduledMessageDraft from 'bundles/enterprise-admin-messages/models/EnterpriseScheduledMessageDraft';
import type { PresetFilter } from 'bundles/enterprise-admin-user-filters/constants';
import type { AttributeSelections, User } from 'bundles/enterprise-admin-users/types';

export enum FilterOptionType {
  COURSE = 'COURSE',
  PROGRAM = 'PROGRAM',
  SPECIALIZATION = 'SPECIALIZATION',
  CERTIFICATE = 'CERTIFICATE',
  CONTRACT = 'CONTRACT',
  BUSINESS_UNIT = 'BUSINESS_UNIT',
  LOCATION = 'LOCATION',
  MANAGER = 'MANAGER',
  JOB_TITLE = 'JOB_TITLE',
}

/**
 * FilterContext
 * A series of keywords for deciding which CTA / tags are available for current filter/preset combination.
 * The word "context" means that it is something making others reasonable.
 * For example:
 *   * 'program' context makes "PROGRAM_NAME" tag reasonable. w/o a program, PROGRAM_NAME does not make sense
 */
export type FilterContext = 'program' | 'enrollment' | 'invitee' | 'learner' | 'allow-empty';

/**
 * FilterContextProvider
 * A provider provides some contexts. For example, "Learning program" filter provides a 'program' context.
 * Presets and FilterCtrls are providers.
 */
export type FilterContextProvider = {
  provideFilterContexts: FilterContext[];
};

/**
 * FilterContextConsumer
 * A consumer "requires" some contexts to be reasonable. For example, "PROGRAM_NAME" requires "program".
 * CTAs and Tags are consumers.
 */
export type FilterContextConsumer = {
  consumeFilterContexts: FilterContext[];
};

/**
 * Types for hidden filters
 */
export enum QueryType {
  MEMBERS = 'MEMBERS',
  INVITEES = 'INVITEES',
  SKILLS_REPORT = 'SKILL_OBJECTIVE_REPORT',
  ADMIN_USER_FILTER = 'ADMIN_USER_FILTER',
}

export enum InvitationState {
  ACTIVE = 'ACTIVE',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export enum OperatorType {
  GREATER_THAN = 'GREATER_THAN',
  LESS_THAN = 'LESS_THAN',
  EQUALS = 'EQUALS',
}

type IntegerFilter = {
  operator: OperatorType;
  value: number;
};

export const SkillProgressFlag = {
  interested: 'INTERESTED',
  achievedTarget: 'ACHIEVED_TARGET',
  madeProgress: 'MADE_PROGRESS',
  completedLevelSet: 'COMPLETED_A_LEVEL_SET',
  notCompletedLevelSet: 'NOT_COMPLETED_ANY_LEVEL_SET',
} as const;

export type SkillProgressFlagValues = (typeof SkillProgressFlag)[keyof typeof SkillProgressFlag];
export type SkillProgressFlagKeys = keyof typeof SkillProgressFlag;

/**
 * Types used for querying Filter API.
 */

export type FilterParams = {
  invitationStates?: InvitationState[];
  productIds?: string[];
  orgId: string;
  programIds?: string[];
  contractIds?: string[];
  completionTimes?: IntegerFilter[];
  businessUnits?: string[];
  locations?: string[];
  managers?: string[];
  jobTitles?: string[];
  searchNamesOrEmails?: string[];
  userIdentifiers?: string[];
  latestActivityAt?: IntegerFilter[];
  numEnrollments?: IntegerFilter[];
  numCompletions?: IntegerFilter[];
  deletedAt?: IntegerFilter[];
  // Skills
  targetSkillProfileIds?: string[];
  skillIds?: string[];
  skillProgressFlags?: SkillProgressFlagValues[];
  skillScores?: IntegerFilter[];
  // Enhanced filters
  programJoinedAt?: IntegerFilter[];
  productEnrolledAt?: IntegerFilter[];
  filterId?: string;
};

/**
 * Types used for viewing Sent Messages.
 */

type EnrichedFilterParams = Omit<FilterParams, 'productIds' | 'contractIds' | 'programIds'> & {
  products: {
    productId: string;
    productName: string;
  }[];
  programs: {
    programId: string;
    programName: string;
  }[];
  contracts: {
    contractId: string;
    contractName: string;
  }[];
};

export enum SortByDirection {
  asc = 'ASC',
  desc = 'DESC',
}

export type SortBy = {
  column: AttributeSelections;
  direction: SortByDirection;
};

export enum Flags {
  programLevel = 'PROGRAM_LEVEL',
  uncompletedEnrollment = 'UNCOMPLETED_ENROLLMENT',
  includeJoinDateInLatestActivityTimestamp = 'INCLUDE_JOIN_DATE_IN_LATEST_ACTIVITY_TIMESTAMP',
}

export type FilterQueryData = {
  queryType: QueryType;
  params: FilterParams;
  sortBy: SortBy[];
  attributeSelection: string[];
  flags?: Flags[];
};

export type MessageData = {
  flags?: Flags[];
  presetId: PresetId;
  filters: Filters;
  subjectValue?: string;
  cml?: CmlContent;
  callToActionId?: CallToActionId;
  callToActionTextOverride?: string;
  senderName?: string;
  replyToEmailAddress?: string;
  emailDraft?: EnterpriseScheduledMessageDraft;
  sentAt?: number;
  userFilter?: PresetFilter;
};

export type AutomatedMessage = {
  id: string;
  title: string;
  caption: string;
  sentAt: string;
};

export type SentMessage = {
  id: string;
  senderName: string;
  subject: string;
  numberOfRecipients: number;
  preset: PresetId;
  sentAt: number;
  replyTo: string;
  enrichedFilterParams: EnrichedFilterParams;
  filterParams: {
    userIdentifiers: string[];
    filterId?: string;
  };
  cmlContent: CmlContent;
  callToAction: CallToActionId;
  htmlContent: string;
  recipientsFileUrl: string;
  status: string;
  authorEmail?: string;
  authorName?: string;
  authorUserId?: number;
};

export const presetIds = [
  'remind-join',
  'remind-enroll',
  'remind-complete',
  'remind-complete-past-30-days',
  'remind-be-active-past-30-days',
  'remind-be-active-general-past-30-days',
  'remind-be-active-past-90-days',
  'congratulate-achievement',
  'custom',
  'deleted',
  'remind-assigned-learners',
] as const;

export type PresetId = (typeof presetIds)[number];

export type EmailTemplate = {
  subject: string;
  initialCML: CmlContent;
};

export type Preset = {
  id: PresetId;
  title: string;
  caption: string;
  description: string;
  userListLabel: string;
  emailTemplate: EmailTemplate;
  availableFilters: FilterType[];
  userFilter: PresetFilter;
  defaultColumns: AttributeSelections[];
  defaultSortBy: SortBy[];
} & FilterContextProvider;

export type UsersListOnlyPreset = Pick<
  Preset,
  'id' | 'userListLabel' | 'availableFilters' | 'userFilter' | 'defaultColumns' | 'defaultSortBy'
>;

export enum PersonalizationId {
  LEARNER_NAME = 'LEARNER_NAME',
  PROGRAM_NAME = 'PROGRAM_NAME',
  ENROLLMENT_NAME = 'ENROLLMENT_NAME',
}

export type PersonalizationTag = {
  value: PersonalizationId;
  caption: string;
} & FilterContextConsumer;

export enum CallToActionId {
  JOIN_NOW = 'JOIN_PROGRAM',
  CONTINUE_LEARNING = 'CONTINUE_LEARNING',
  FIND_A_COURSE = 'FIND_A_COURSE',
  DO_NOT_INCLUDE = 'DO_NOT_INCLUDE',
}

export type CallToActionPreset = {
  id: CallToActionId;
  title: string;
  caption: string;
} & FilterContextConsumer;

export type FilterOptionTypeName = {
  full: string;
  plural: string;
};

export type CreateMessageParams = {
  presetId: PresetId;
  filters: Filters;
  users: User[];
  usersCount: number;
  isProgramScope?: boolean;
  searchNamesOrEmails?: string;
  refetchUsersCount?: boolean;
  useNewUserFilter?: boolean;
  emailTemplate?: EmailTemplate;
  callToActionId?: CallToActionId;
};
